import { Fragment, useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { ResponsiveLine } from '@nivo/line';
import GraphTopContent from '../Common/GraphTopContent';
import { NoRecordsFound, NoGraphsAvailable } from '../../NoRecordsFound';
import Loader from '../../../../components/Common/Loader';

import { wholeNumberParams } from '../Common/Constants';

import {
  uniqueIdentViewsVariables,
  getLineData,
  getXAxisTitle,
  setLineXMargins,
  lineTheme,
} from './LineHelpers';

import useWindowsDimensions from '../../../../hooks/useWindowsDimensions';
import useLineChart from '../../graphHooks/UseLineChart';

import html2canvas from 'html2canvas';
import SharedButton from '@/global/Button/Button';
import { ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { userAppContext } from '../../../../context/Context';
import { HandRaisedIcon } from '@heroicons/react/24/outline';

interface LineChartProps {
  source: any;
  keySearch: any;
  view: any;
  names: any;
  chartInfo: any;
  setChartInfo: any;
  baseUrl: any;
  parent: any;
  lineSearchVariables: any;
}

function LineChart({
  source,
  keySearch,
  view,
  names,
  chartInfo,
  setChartInfo,
  baseUrl,
  parent,
  lineSearchVariables,
}: LineChartProps) {
  // Data necessary for using the custom hook
  // empty array as the initial data for the chart
  const initialChartData: any[] = [];
  const { restrictGridResultCount } = userAppContext();
  const defaultPageSize = restrictGridResultCount || 20;
  const sampleSizeOptions = [20, 50, 100, 250, 'ALL'];
  const [sampleSize, setSampleSize] = useState<number | 'ALL'>(defaultPageSize);
  const requestPageSize = sampleSize === 'ALL' ? -1 : sampleSize;
  const fetchAll = sampleSize === 'ALL';
  const [showPanningDropdown, setShowPanningDropdown] = useState(false);
  const panningDropdownRef = useRef<HTMLDivElement>(null);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const actionsDropdownRef = useRef<HTMLDivElement>(null);
  // current width
  const { width } = useWindowsDimensions();

  // calling the useHTTPSingleFilter hook
  const { chartData, yAxisValue, isPayloadEmpty, isLoading, setXMargin, totalResults } = useLineChart({
    chartInfo,
    initialChartData,
    keySearch,
    source,
    baseUrl,
    parent,
    view,
    getLineData,
    width,
    uniqueIdentViewsVariables,
    pageSize: requestPageSize,
  });

  // check if the chart is empty
  const isChartDataEmpty = chartData.length === 0;
  const visibleResults = fetchAll
    ? totalResults || requestPageSize
    : Math.min(requestPageSize, totalResults || requestPageSize);
  const allPoints = chartData?.[0]?.data || [];
  const maxPointIndex = Math.max(allPoints.length - 1, 0);
  const [rangeStart, setRangeStart] = useState(0);
  const [rangeEnd, setRangeEnd] = useState(0);
  const [selectedPointX, setSelectedPointX] = useState<string | null>(null);

  useEffect(() => {
    const defaultEnd = Math.min(maxPointIndex, 49);
    setRangeStart(0);
    setRangeEnd(defaultEnd);
    setSelectedPointX(null);
  }, [maxPointIndex, chartInfo.variable]);

  const filteredChartData = useMemo(() => {
    if (!chartData?.length) return chartData;
    return chartData.map(series => ({
      ...series,
      data: series.data.slice(rangeStart, rangeEnd + 1),
    }));
  }, [chartData, rangeStart, rangeEnd]);

  // set the X axis title
  const originalXAxisTitle = uniqueIdentViewsVariables[parent]?.plottedValues[view];
  const additionalTitleData = {
    numberOfFilters: names.length,
    chartData: chartData,
  };
  const xAxisTitle = getXAxisTitle(originalXAxisTitle, additionalTitleData);

  // create a custom tooltip for each point in the graph
  const lineTooltip = useCallback(
    ({ point }: any) => {
      return (
        <div className="custom-tooltip-heatmap">
          <span>
            <em>Posicion:</em> <strong>{point.indexInSeries + 1}</strong>
          </span>
          <br />
          <span>
            <em>{point.data.x}</em>
          </span>
          <br />
          <span>
            <em>{chartInfo.variableName}:</em> <strong>{point.data.y}</strong>
          </span>
        </div>
      );
    },
    [chartInfo],
  );

  // adjust the X margins when the width changes
  useEffect(() => {
    if (chartData.length > 0) {
      const totalResults = chartData[0].data.length;
      setXMargin(() => setLineXMargins(width, totalResults));
    }
  }, [width]);

  const panRange = (direction: 'left' | 'right') => {
    const windowSize = rangeEnd - rangeStart + 1;
    if (windowSize <= 0 || allPoints.length <= windowSize) return;
    const step = Math.max(Math.floor(windowSize / 3), 1);
    if (direction === 'left') {
      const nextStart = Math.max(0, rangeStart - step);
      setRangeStart(nextStart);
      setRangeEnd(nextStart + windowSize - 1);
    } else {
      const nextEnd = Math.min(maxPointIndex, rangeEnd + step);
      setRangeEnd(nextEnd);
      setRangeStart(nextEnd - windowSize + 1);
    }
  };

  function downloadJSON() {
    const JSONcomplete = JSON.stringify(chartData);
    const jsonURL = `data:text/json;charset=utf-8,${encodeURIComponent(JSONcomplete)}`;

    const link = document.createElement('a');
    link.href = jsonURL;
    link.download = 'HEATMAP.JSON';
    link.click();
  }

  const downloadPNG = () => {
    const chartContainer = document.querySelector('.chart-container-75') as HTMLElement;
    if (chartContainer) {
      html2canvas(chartContainer).then(function (canvas) {
        const link = document.createElement('a');
        link.download = 'chart.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      });
    }
  };

  return (
    <section className="mb-8">
      {isLoading && (
        <div className="row">
          {' '}
          <Loader />
        </div>
      )}
      {!isPayloadEmpty && !isLoading && (
        <Fragment>
          {!isChartDataEmpty && (
            <Fragment>
              <div className="graph-container">
                <GraphTopContent
                  names={names}
                  view={view}
                  graphSearchVariables={lineSearchVariables}
                  chartInfo={chartInfo}
                  setChartInfo={setChartInfo}
                  parent={parent}
                  resultsNumber={visibleResults}
                  totalResults={totalResults}
                  selectedSampleSize={sampleSize}
                  onSampleSizeChange={setSampleSize}
                  sampleSizeOptions={sampleSizeOptions}
                />
                <div className="graph-interactions">
                  <div className="row items-center justify-between w-full">
                    <div className="graph-interactions">
                      <div style={{ position: 'relative' }} ref={panningDropdownRef}>
                        <SharedButton
                          variant="secondaryOutline"
                          onClick={() => {
                            setShowPanningDropdown(prev => !prev);
                          }}
                          className="mx-0"
                        >
                          <HandRaisedIcon className="w-5 mr-2" />
                          Panning
                          <ChevronDownIcon
                            className="w-4 ml-2 transition-transform duration-200"
                            style={{ transform: showPanningDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
                          />
                        </SharedButton>
                        {showPanningDropdown && (
                          <div className="absolute top-full left-0 mt-1 bg-white shadow-md rounded-md z-50 p-2 min-w-max flex flex-col items-center w-full">
                            <span
                              className="cursor-pointer row items-center justify-center font-bold p-2 rounded-md text-blue w-full hover:bg-blue-100"
                              onClick={() => panRange('left')}
                            >
                              Left Pan
                            </span>
                            <span
                              className="cursor-pointer row items-center justify-center font-bold p-2 rounded-md text-blue w-full hover:bg-blue-100"
                              onClick={() => panRange('right')}
                            >
                              Right Pan
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="graph-range-controls">
                        <span className="graph-range-label">Visible points:</span>
                        <div className="row items-center">
                          <input
                            type="range"
                            min={0}
                            max={maxPointIndex}
                            value={rangeStart}
                            onChange={e => {
                              const next = Number(e.target.value);
                              setRangeStart(Math.min(next, rangeEnd));
                            }}
                          />
                          <input
                            type="range"
                            min={0}
                            max={maxPointIndex}
                            value={rangeEnd}
                            onChange={e => {
                              const next = Number(e.target.value);
                              setRangeEnd(Math.max(next, rangeStart));
                            }}
                          />
                          <span className="graph-range-window ml-2">
                            {rangeStart + 1}-{rangeEnd + 1} / {allPoints.length}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div style={{ position: 'relative' }} ref={actionsDropdownRef}>
                      <SharedButton
                        variant="secondaryOutline"
                        onClick={() => {
                          setShowActionsDropdown(prev => !prev);
                        }}
                        className="mr-0"
                      >
                        <ArrowDownTrayIcon className="w-5 mr-2" />
                        Download
                        <ChevronDownIcon
                          className="w-4 ml-2 transition-transform duration-200"
                          style={{ transform: showActionsDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        />
                      </SharedButton>
                      {showActionsDropdown && (
                        <div className="absolute top-full left-0 mt-1 bg-white shadow-md rounded-md z-50 p-2 min-w-max flex flex-col items-center w-full">
                          <span
                            className="cursor-pointer row items-center justify-center hover:bg-blue-100 font-bold p-2 rounded-md text-blue w-full"
                            onClick={() => {
                              downloadJSON();
                              setShowActionsDropdown(false);
                            }}
                          >
                            JSON
                          </span>
                          <span
                            className="cursor-pointer row items-center justify-center hover:bg-blue-100 font-bold p-2 rounded-md text-blue w-full"
                            onClick={() => {
                              downloadPNG();
                              setShowActionsDropdown(false);
                            }}
                          >
                            PNG
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className={`chart-container-75`}>
                  <ResponsiveLine
                    data={filteredChartData}
                    margin={{
                      top: 80,
                      right: 250,
                      bottom: 290,
                      left: 130,
                    }}
                    theme={lineTheme}
                    colors={() => '#336575'}
                    lineWidth={0}
                    tooltip={lineTooltip}
                    useMesh={true}
                    onClick={datum => {
                      if ('data' in datum && datum.data?.x !== undefined) {
                        setSelectedPointX(String(datum.data.x));
                      }
                    }}
                    xScale={{ type: 'point' }}
                    yScale={{
                      type: 'linear',
                      min: yAxisValue.min,
                      max: yAxisValue.max,
                      stacked: false,
                      reverse: false,
                    }}
                    axisTop={{
                      legend: `${xAxisTitle}`,
                      legendOffset: -40,
                      legendPosition: 'middle',
                      renderTick: () => {
                        return (
                          <g>
                            <text>{``}</text>
                          </g>
                        );
                      },
                    }}
                    axisRight={null}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 15,
                      renderTick: (tick: any) => {
                        return (
                          <g transform={`translate(${tick.x},${tick.y})`}>
                            <text
                              transform="rotate(-55)"
                              textAnchor="end"
                              dominantBaseline="middle"
                              className="axisLegend"
                              style={{ fontSize: 12 }}
                            >
                              {tick.value}
                            </text>
                          </g>
                        );
                      },
                    }}
                    axisLeft={{
                      format: (value: any) => {
                        if (
                          wholeNumberParams.includes(chartInfo.variable) &&
                          value.toString().includes('.')
                        ) {
                          return '';
                        }
                        return value;
                      },
                      tickSize: 5,
                      tickPadding: 15,
                      tickRotation: 0,
                      legend: `${chartInfo.variableFullName}`,
                      legendOffset: -80,
                      legendPosition: 'middle',
                    }}
                    pointSize={selectedPointX ? 12 : 15}
                    pointColor={(point: any) =>
                      selectedPointX && point.data.x !== selectedPointX
                        ? 'rgba(10,16,79,0.3)'
                        : 'rgb(10,16,79)'
                    }
                    pointBorderWidth={3}
                    pointBorderColor={(point: any) =>
                      selectedPointX && point.data.x === selectedPointX ? 'rgb(190,0,128)' : 'rgb(0,15,45)'
                    }
                    pointLabelYOffset={-12}
                  />
                </div>
              </div>
            </Fragment>
          )}
          {isChartDataEmpty && (
            <Fragment>
              <GraphTopContent
                names={names}
                view={view}
                graphSearchVariables={lineSearchVariables}
                chartInfo={chartInfo}
                setChartInfo={setChartInfo}
                parent={parent}
                resultsNumber={0}
                totalResults={totalResults}
                selectedSampleSize={sampleSize}
                onSampleSizeChange={setSampleSize}
                sampleSizeOptions={sampleSizeOptions}
              />
              <NoGraphsAvailable />
            </Fragment>
          )}
        </Fragment>
      )}
      {isPayloadEmpty && !isLoading && (
        <div className="row">
          {' '}
          <NoRecordsFound />
        </div>
      )}
    </section>
  );
}

export default LineChart;
